/**
 * ThesesPane — list user's theses (open + closed) with liquidate action.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useToast } from '../lib/toast';
import { useConfirm } from '../lib/confirm';
import { useFocal } from '../lib/focal';
import { useNavigate } from 'react-router-dom';

export function ThesesPane() {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const focal = useFocal();
  const [theses, setTheses] = useState<api.EngineerThesis[]>([]);
  const [loading, setLoading] = useState(true);
  const [liquidating, setLiquidating] = useState<string | null>(null);

  async function refresh() {
    try {
      const r = await api.engineer.listTheses();
      setTheses(r.theses ?? []);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : String(e), 'bad');
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  async function liquidate(id: string) {
    const yes = await confirm(
      'Liquidate this thesis? All positions will be marked for sale; sell orders queue to the Morning Brief for approval.',
      { danger: true, confirmLabel: 'Liquidate' },
    );
    if (!yes) return;
    setLiquidating(id);
    try {
      const r = await api.engineer.liquidate(id);
      toast.show(r.message, 'ok');
      await refresh();
    } catch (e) {
      toast.show(e instanceof Error ? e.message : String(e), 'bad');
    } finally {
      setLiquidating(null);
    }
  }

  if (loading) return <div className="text-sm text-ink-mute">loading theses...</div>;

  const open = theses.filter((t) => t.status === 'open');
  const closed = theses.filter((t) => t.status !== 'open');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-mute">
          {open.length} open · {closed.length} closed
        </div>
        <button onClick={() => navigate('/research?wizard=new')} className="btn text-xs border-accent">
          + new thesis
        </button>
      </div>

      {theses.length === 0 && (
        <div className="card p-3 text-xs text-ink-mute">
          No theses yet. Use the wizard to design your first one.
        </div>
      )}

      <div className="space-y-2">
        {theses.map((t) => (
          <div key={t.id} className="card p-3 overflow-hidden" data-testid={`thesis-row-${t.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <button
                className="min-w-0 flex-1 text-left hover:opacity-80"
                onClick={() => focal.set({ kind: 'thesis_detail', thesisId: t.id })}
                data-testid={`thesis-open-detail-${t.id}`}
                title="Open thesis detail"
              >
                <div className="font-semibold text-sm break-words underline-offset-2 hover:underline">
                  {t.symbol ?? '(multi-symbol)'}{' '}
                  <span className="text-ink-mute font-normal text-xs ml-2">
                    {t.archetype} · {t.positions.length} position{t.positions.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="text-[11px] text-ink-mute mt-0.5 num break-words">
                  p={t.p_success.toFixed(2)} · q={t.q_failure.toFixed(2)} ·
                  conviction {t.conviction} · horizon {t.horizon_days}d
                </div>
              </button>
              <div className="flex items-center gap-2">
                <span className={`pill text-[10px] ${t.status === 'open' ? 'text-accent' : 'text-ink-mute'}`}>
                  {t.status}
                </span>
                {t.status === 'open' && (
                  <button onClick={() => liquidate(t.id)} disabled={liquidating === t.id} className="btn text-xs">
                    {liquidating === t.id ? 'closing...' : 'liquidate'}
                  </button>
                )}
              </div>
            </div>
            {t.positions.length > 0 && (
              <div className="mt-2 text-[11px] text-ink-mute flex flex-wrap gap-x-3 gap-y-0.5">
                {t.positions.map((p) => (
                  <span key={p.symbol}>
                    <span className="num">{p.symbol}</span>: {p.target_weight_pct.toFixed(1)}%
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
